import React, { useMemo, useState } from 'react';
import { CopyOutlined } from '@duxton-web/icons';
import { Button, Modal, Select } from '@duxton-web/ui';
import { useParams } from 'react-router-dom';

import { isCreateMode } from '../../constants';
import {
  useExperimentContext,
  useExperimentDispatchContext,
} from '../../contexts/experiment-context';
import { cloneConfiguration } from '../../store/actions/experiment/action-creators';
import { getCountryNameByCountryCode } from '@/common/CountryCodesMap';

const CloneConfigurationModal = ({
  index,
  open,
  onCancel,
  onOk,
}: {
  index: number;
  open: boolean;
  onCancel?: () => void;
  onOk?: () => void;
}) => {
  const experimentContext = useExperimentContext();
  const experimentDispatch = useExperimentDispatchContext();
  const { configurations } = experimentContext;

  const [selectedConfigIndex, setSelectedConfigIndex] = useState<number | null>(
    null,
  );

  const options = useMemo(() => {
    return configurations
      .map((c, i: number) => {
        if (i !== index)
          return {
            value: i,
            label: c.country_code
              ? getCountryNameByCountryCode(c.country_code)
              : `Configuration ${i + 1}`,
          };
      })
      .filter((o) => o);
  }, [configurations, index]);

  if (!open) return null;

  return (
    <Modal
      data-testid="clone-configuration-modal"
      centered
      width={700}
      maskClosable={false}
      okButtonProps={{
        size: 'small',
        disabled: selectedConfigIndex == null,
      }}
      cancelButtonProps={{
        size: 'small',
      }}
      open={open}
      onCancel={onCancel}
      onOk={() => {
        experimentDispatch(cloneConfiguration(selectedConfigIndex, index));

        onOk();
      }}
      title="Clone Configuration Values From..."
      okText="Copy Configuration Values"
    >
      <Select
        options={options}
        style={{ width: 250 }}
        allowClear
        onChange={(value) => setSelectedConfigIndex(value)}
      />
    </Modal>
  );
};

const CloneConfiguration = ({ index }: { index: number }) => {
  const params = useParams<{ action: string }>();
  const action = params.action;

  const experimentContext = useExperimentContext();
  const { configurations } = experimentContext;

  const [open, setOpen] = useState<boolean>(false);

  if (configurations.length < 2 || !isCreateMode(action)) return null;

  return (
    <>
      <Button
        size="small"
        type="secondary"
        icon={<CopyOutlined />}
        onClick={() => setOpen(true)}
      >
        Copy Configuration Values From...
      </Button>
      <CloneConfigurationModal
        index={index}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => setOpen(false)}
      />
    </>
  );
};

export default CloneConfiguration;
